import { Select } from '@mantine/core';
import { useMemo } from 'react';

interface ParameterSelectorProps {
  /** Currently selected parameter value (e.g., "dkh", "temp") */
  value: string | null;
  /** Called when parameter selection changes */
  onChange: (paramValue: string | null) => void;
  /** Available parameters for the current tank (fetched from backend) */
  parameters: Array<{ value: string; label: string }>;
  /** Loading state while fetching parameters */
  isLoading?: boolean;
}

/**
 * Parameter selector dropdown.
 *
 * Shows parameters that have actual data for the current tank.
 * Parameter list is dynamic - fetched from backend based on measurements table.
 * Source filtering is handled separately via SourceFilter component.
 */
export function ParameterSelector({
  value,
  onChange,
  parameters,
  isLoading = false,
}: ParameterSelectorProps) {
  // Use parameters from props (dynamic, based on what data exists for this tank)
  // Memoized to avoid re-mapping on every render
  // Filter out any invalid entries that don't have required value property
  const selectData = useMemo(
    () =>
      parameters
        .filter(param => param && typeof param.value === 'string' && param.value.length > 0)
        .map(param => ({
          value: param.value,
          label: param.label || param.value,
        })),
    [parameters]
  );

  return (
    <Select
      label="Parameter"
      placeholder={isLoading ? 'Loading parameters...' : 'Select parameter to edit'}
      data={selectData}
      value={value}
      onChange={onChange}
      searchable
      clearable={false}
      disabled={isLoading || parameters.length === 0}
      styles={{
        label: { fontWeight: 600, marginBottom: 8 },
      }}
    />
  );
}
